import { useMemo } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Type, ChevronsUpDown } from "lucide-react";

/**
 * Éditeur de blocs de contenu (dashboard).
 *
 * `value` = tableau de blocs `{ id, title, content, mode }`.
 * Rétro-compatibilité : si `value` est vide et `legacyBody` fourni, on affiche
 * un bouton d'import 1-clic qui convertit l'ancien champ « Contenu détaillé »
 * en un premier bloc « Texte normal ».
 */
export default function BlocksEditor({ value, legacyBody = "", onChange }) {
  const blocks = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const newBlock = () => ({
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `blk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: "",
    content: "",
    mode: "text",
  });

  const addBlock = () => onChange([...blocks, newBlock()]);

  const updateBlock = (idx, patch) => {
    const next = blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    onChange(next);
  };

  const removeBlock = (idx) => {
    if (!window.confirm("Supprimer définitivement ce bloc ?")) return;
    onChange(blocks.filter((_, i) => i !== idx));
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const importLegacy = () => {
    if (!legacyBody) return;
    const parts = legacyBody.split(/\n{2,}/).filter((p) => p.trim());
    const imported = parts.map((p, i) => ({
      ...newBlock(),
      title: parts.length > 1 ? `Section ${i + 1}` : "",
      content: p.trim(),
      mode: "text",
    }));
    onChange(imported.length ? imported : [newBlock()]);
  };

  return (
    <div className="space-y-3" data-testid="blocks-editor">
      {blocks.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center bg-slate-50/50">
          <p className="text-sm text-slate-600">
            Aucun bloc pour cette page. Créez un nouveau bloc ou importez l'ancien contenu détaillé.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 justify-center">
            <button type="button" onClick={addBlock} className="btn-primary text-sm" data-testid="btn-add-block">
              <Plus className="w-4 h-4" /> Ajouter un premier bloc
            </button>
            {legacyBody && (
              <button
                type="button"
                onClick={importLegacy}
                className="btn-secondary text-sm"
                data-testid="btn-import-legacy"
              >
                <ChevronsUpDown className="w-4 h-4" /> Importer l'ancien contenu
              </button>
            )}
          </div>
        </div>
      )}

      {blocks.map((b, idx) => (
        <div
          key={b.id || idx}
          className="paper-card p-4 border-l-4 border-l-unisic-gold"
          data-testid={`block-${idx}`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Type className="w-3.5 h-3.5 text-unisic-gold" /> Bloc {idx + 1}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="p-1.5 text-slate-500 hover:text-unisic-blue disabled:opacity-30 disabled:cursor-not-allowed"
                title="Monter"
                data-testid={`block-up-${idx}`}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === blocks.length - 1}
                className="p-1.5 text-slate-500 hover:text-unisic-blue disabled:opacity-30 disabled:cursor-not-allowed"
                title="Descendre"
                data-testid={`block-down-${idx}`}
              >
                <ArrowDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => removeBlock(idx)}
                className="p-1.5 text-slate-400 hover:text-unisic-red"
                title="Supprimer"
                data-testid={`block-del-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] md:items-start">
            <input
              value={b.title || ""}
              onChange={(e) => updateBlock(idx, { title: e.target.value })}
              placeholder="Titre du bloc (optionnel si mode texte normal)"
              className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm font-semibold"
              data-testid={`block-title-${idx}`}
            />
            <select
              value={b.mode || "text"}
              onChange={(e) => updateBlock(idx, { mode: e.target.value })}
              className="border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white min-w-[190px]"
              data-testid={`block-mode-${idx}`}
            >
              <option value="text">Mode : Texte normal</option>
              <option value="accordion">Mode : Accordéon (déroulant)</option>
            </select>
          </div>

          <textarea
            value={b.content || ""}
            onChange={(e) => updateBlock(idx, { content: e.target.value })}
            rows={7}
            placeholder="Contenu du bloc (séparez les paragraphes par une ligne vide)"
            className="mt-3 w-full border border-slate-300 px-3 py-2 rounded-sm text-sm"
            data-testid={`block-content-${idx}`}
          />

          {b.mode === "accordion" && !b.title?.trim() && (
            <div className="mt-2 text-[11px] text-amber-700">
              Astuce : donnez un titre au bloc — c'est ce que verront les visiteurs avant de cliquer sur « Lire plus ».
            </div>
          )}
        </div>
      ))}

      {blocks.length > 0 && (
        <button
          type="button"
          onClick={addBlock}
          className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-unisic-blue hover:text-unisic-gold"
          data-testid="btn-add-block"
        >
          <Plus className="w-4 h-4" /> Ajouter un bloc
        </button>
      )}
    </div>
  );
}
